import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './RechargeForm.css';

/**
 * 充值表单组件
 * 
 * @param {Object} props
 * @param {Function} props.onClose - 关闭回调
 * @param {Function} props.onSuccess - 成功回调
 */
const RechargeForm = ({ courtId, onClose, onSuccess }) => {
  const [courts, setCourts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    courtId: courtId || '',
    operationDate: new Date().toISOString().split('T')[0],
    rechargeAmount: '',
    courtTime: '',
    description: '',
    subAccountId: '',
    allocateAmount: ''
  });
  const [calculatedCourtTime, setCalculatedCourtTime] = useState(null);
  const [isCourtTimeManuallyModified, setIsCourtTimeManuallyModified] = useState(false);

  const baseUrl = getApiBaseUrl();

  // 获取场地列表
  useEffect(() => {
    const fetchCourts = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/court-usage/courts?isActive=true`);
        if (response.data.success) {
          setCourts(response.data.data || []);
          if (courtId) {
            setFormData(prev => ({ ...prev, courtId }));
          } else if (response.data.data && response.data.data.length > 0) {
            setFormData(prev => ({ ...prev, courtId: response.data.data[0]._id }));
          }
        }
      } catch (err) {
        console.error('获取场地列表失败:', err);
      }
    };
    fetchCourts();
  }, [courtId]);

  // 获取子账户列表（按场地）
  useEffect(() => {
    const fetchSubAccounts = async () => {
      if (!formData.courtId) {
        setSubAccounts([]);
        return;
      }
      
      try {
        const response = await axios.get(`${baseUrl}/api/court-usage/sub-accounts`, {
          params: { courtId: formData.courtId, isActive: true }
        });
        if (response.data.success) {
          setSubAccounts(response.data.data || []);
        }
      } catch (err) {
        console.error('获取子账户列表失败:', err);
      }
    };
    fetchSubAccounts();
  }, [formData.courtId]);

  // 计算场时数
  useEffect(() => {
    if (formData.courtId && formData.rechargeAmount) {
      const court = courts.find(c => c._id === formData.courtId);
      if (court && court.rechargeRate) {
        const rechargeAmountNum = parseFloat(formData.rechargeAmount);
        if (!isNaN(rechargeAmountNum) && rechargeAmountNum > 0) {
          const calculated = (rechargeAmountNum / court.rechargeRate).toFixed(2);
          setCalculatedCourtTime(calculated);
          // 只有在用户没有手动修改过场时数时，才自动更新
          if (!isCourtTimeManuallyModified) {
            setFormData(prev => ({ ...prev, courtTime: calculated }));
          }
        }
      }
    } else {
      setCalculatedCourtTime(null);
      if (!isCourtTimeManuallyModified) {
        setFormData(prev => ({ ...prev, courtTime: '' }));
      }
    }
  }, [formData.courtId, formData.rechargeAmount, courts, isCourtTimeManuallyModified]);

  // 当场地改变时，重置手动修改标志
  useEffect(() => {
    setIsCourtTimeManuallyModified(false);
  }, [formData.courtId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.courtId) {
      alert('请选择场地');
      return;
    }
    
    if (!formData.rechargeAmount || parseFloat(formData.rechargeAmount) <= 0) {
      alert('请输入有效的充值金额');
      return;
    }

    try {
      setLoading(true);
      const requestData = {
        courtId: formData.courtId,
        operationDate: formData.operationDate,
        rechargeAmount: parseFloat(formData.rechargeAmount),
        courtTime: formData.courtTime ? parseFloat(formData.courtTime) : undefined,
        description: formData.description
      };
      
      // 如果选择了子账户，添加关联信息
      if (formData.subAccountId) {
        requestData.subAccountId = formData.subAccountId;
        if (formData.allocateAmount) {
          requestData.allocateAmount = parseFloat(formData.allocateAmount);
        }
      }
      
      const response = await axios.post(`${baseUrl}/api/court-usage/main-account/recharge`, requestData);

      if (response.data.success) {
        onSuccess();
      } else {
        throw new Error(response.data.message || '充值失败');
      }
    } catch (err) {
      console.error('充值失败:', err);
      alert(err.response?.data?.message || err.message || '充值失败');
    } finally {
      setLoading(false);
    }
  };

  const selectedCourt = courts.find(c => c._id === formData.courtId);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>添加充值记录</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="recharge-form">
          {!courtId && (
            <div className="form-group">
              <label>场地 *</label>
              <select
                value={formData.courtId}
                onChange={(e) => {
                  setIsCourtTimeManuallyModified(false);
                  setFormData({ ...formData, courtId: e.target.value, courtTime: '' });
                }}
                required
              >
                <option value="">请选择场地</option>
                {courts.map(court => (
                  <option key={court._id} value={court._id}>
                    {court.name} ({court.rechargeRate}元/场时)
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>操作日期 *</label>
            <input
              type="date"
              value={formData.operationDate}
              onChange={(e) => setFormData({ ...formData, operationDate: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>充值金额（元） *</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.rechargeAmount}
              onChange={(e) => setFormData({ ...formData, rechargeAmount: e.target.value })}
              placeholder="请输入充值金额"
              required
            />
            {selectedCourt && calculatedCourtTime && (
              <div className="form-hint">
                根据换算率自动计算: {calculatedCourtTime} 场时
              </div>
            )}
          </div>

          <div className="form-group">
            <label>场时数（可手动修改）</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.courtTime}
              onChange={(e) => {
                setIsCourtTimeManuallyModified(true);
                setFormData({ ...formData, courtTime: e.target.value });
              }}
              placeholder={calculatedCourtTime || "自动计算"}
            />
            {selectedCourt && (
              <div className="form-hint">
                换算率: {selectedCourt.rechargeRate} 元/场时
                {calculatedCourtTime && isCourtTimeManuallyModified && (
                  <span style={{ marginLeft: '12px', color: '#1a6eb0', cursor: 'pointer' }}
                        onClick={() => {
                          setIsCourtTimeManuallyModified(false);
                          setFormData(prev => ({ ...prev, courtTime: calculatedCourtTime }));
                        }}>
                    恢复自动计算值 ({calculatedCourtTime})
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>关联子账户（可选）</label>
            <select
              value={formData.subAccountId}
              onChange={(e) => setFormData({ ...formData, subAccountId: e.target.value, allocateAmount: '' })}
            >
              <option value="">不关联子账户</option>
              {subAccounts.map(account => (
                <option key={account._id} value={account._id}>
                  {account.name} (余额: {account.currentBalance.toFixed(2)} 场时)
                </option>
              ))}
            </select>
            {formData.subAccountId && (
              <div className="form-hint" style={{ color: '#666', fontSize: '13px', marginTop: '8px' }}>
                选择子账户后，充值将同时增加主账户和子账户的场时
              </div>
            )}
          </div>

          {formData.subAccountId && (
            <div className="form-group">
              <label>子账户增加的场时数（可选，不填则与主账户相同）</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={calculatedCourtTime || ''}
                value={formData.allocateAmount}
                onChange={(e) => setFormData({ ...formData, allocateAmount: e.target.value })}
                placeholder={`最多可设置 ${calculatedCourtTime || 0} 场时`}
              />
              <div className="form-hint" style={{ color: '#666', fontSize: '13px' }}>
                如果不填写，子账户将增加与主账户相同的场时数（主账户不扣除，双加）
              </div>
            </div>
          )}

          <div className="form-group">
            <label>说明</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="请输入说明（可选）"
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              取消
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? '提交中...' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RechargeForm;

